/**
 * QMS User Select Component
 * Searchable combobox for selecting a user to assign to a QMS ticket
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useQmsUserSearch, useQmsUserById } from '../../hooks/useQmsUsers';
import type { QmsUser } from '../../types/qms.types';
import { isPopulatedUser, extractId } from '../../types/qms.types';
import type { QmsUserDetails } from '../../api/qmsApi';

interface QmsUserSelectProps {
  value?: QmsUser | string; // Accept both populated user object and user ID
  onChange: (user: QmsUser | undefined) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Optional aria-label forwarded onto the trigger button for accessible labelling. */
  ariaLabel?: string;
}

type UserLike = QmsUser | QmsUserDetails;

/**
 * Get user initials for avatar display
 */
function getUserInitials(user: UserLike): string {
  const first = user.firstname?.charAt(0) || '';
  const last = user.lastname?.charAt(0) || '';
  return (first + last).toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?';
}

/**
 * Get full user name for display
 */
function getUserDisplayName(user: UserLike): string {
  if (user.firstname || user.lastname) {
    return `${user.firstname || ''} ${user.lastname || ''}`.trim();
  }
  return user.email || 'Unknown User';
}

export function QmsUserSelect({
  value,
  onChange,
  disabled = false,
  className,
  placeholder,
  ariaLabel,
}: QmsUserSelectProps) {
  const { t } = useTranslation('qms');
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch users with server-side search
  const { data: usersData, isLoading: isSearchLoading } = useQmsUserSearch(
    searchQuery || '',
    open // Only search when popover is open
  );

  // Extract user ID from value (handles both populated and unpopulated)
  const selectedUserId = extractId(value);

  // Fetch the selected user separately if it's not in the search results and value is just an ID
  // This handles the case where the component mounts with an existing assignment (string ID)
  const needsUserFetch = !!selectedUserId && !isPopulatedUser(value);
  const { data: fetchedUser } = useQmsUserById(
    selectedUserId,
    needsUserFetch && !usersData?.users.some((u) => u._id === selectedUserId)
  );

  // Get the selected user object
  // Priority: populated value > search results > separately fetched user
  const selectedUser: UserLike | undefined = isPopulatedUser(value)
    ? value
    : usersData?.users.find((u) => u._id === selectedUserId) || fetchedUser || undefined;

  const handleSelect = (user: QmsUser) => {
    onChange(user);
    setOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            className="w-full justify-between h-auto min-h-10"
            disabled={disabled}
          >
            {selectedUser ? (
              <div className="flex items-center gap-2 flex-1 overflow-hidden">
                <div className="w-6 h-6 rounded-full bg-atek-blue/10 text-atek-blue flex items-center justify-center text-xs font-medium shrink-0">
                  {getUserInitials(selectedUser)}
                </div>
                <div className="flex flex-col items-start overflow-hidden">
                  <span className="truncate text-sm">
                    {getUserDisplayName(selectedUser)}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {selectedUser.email}
                  </span>
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {placeholder || t('form.placeholders.selectUser')}
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedUserId && !disabled && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClear(e as unknown as React.MouseEvent);
                    }
                  }}
                  className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                  aria-label="Clear user"
                >
                  <X className="h-3 w-3 text-gray-400" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('form.placeholders.searchUser')}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isSearchLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {t('form.placeholders.searchUser')}...
                </div>
              ) : (
                <>
                  <CommandEmpty>{t('form.placeholders.noUserFound')}</CommandEmpty>
                  <CommandGroup>
                    {usersData?.users.map((user) => (
                      <CommandItem
                        key={user._id}
                        value={`${user.firstname} ${user.lastname} ${user.email}`}
                        onSelect={() => handleSelect(user)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedUserId === user._id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <div className="w-6 h-6 rounded-full bg-atek-blue/10 text-atek-blue flex items-center justify-center text-xs font-medium mr-2">
                          {getUserInitials(user)}
                        </div>
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate text-sm">
                            {getUserDisplayName(user)}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
